import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import CONFIG from '../Services/CONFIG';

// Audio configuration matching backend: PCM 24kHz 16-bit mono
const SAMPLE_RATE = 24000;
const FRAME_SIZE_MS = 20;
const BYTES_PER_FRAME = SAMPLE_RATE * (FRAME_SIZE_MS / 1000) * 2; // 960 bytes per 20ms frame

/**
 * Convert Float32Array audio samples to base64-encoded PCM16 (little-endian)
 */
function float32ToPcm16Base64(float32Array) {
    const pcm16 = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    const bytes = new Uint8Array(pcm16.buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Convert base64-encoded PCM16 (little-endian) to Float32Array
 */
function pcm16Base64ToFloat32(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7FFF);
    }
    return float32;
}

/**
 * Hook for realtime audio WebSocket communication with the agent
 */
export function useRealtimeAudioCall(agentId) {
    const { getAccessTokenSilently } = useAuth0();
    
    const [isCallActive, setIsCallActive] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [callStatus, setCallStatus] = useState('idle'); // idle, connecting, active, error
    const [transcriptText, setTranscriptText] = useState('');
    
    const wsRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const audioQueueRef = useRef([]);
    const isPlayingRef = useRef(false);
    const nextPlayTimeRef = useRef(0);

    // Build WebSocket URL from config
    const getWebSocketUrl = useCallback(() => {
        const baseUrl = CONFIG.SERVER_ASSISTANT;
        // Convert HTTP(S) to WS(S)
        const wsUrl = baseUrl.replace(/^http/, 'ws');
        return `${wsUrl}/v1/agents/realtime/ws`;
    }, []);

    // Play audio chunk
    const playAudioChunk = useCallback((float32Data) => {
        if (!audioContextRef.current) return;
        
        const ctx = audioContextRef.current;
        const buffer = ctx.createBuffer(1, float32Data.length, SAMPLE_RATE);
        buffer.getChannelData(0).set(float32Data);
        
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        
        const now = ctx.currentTime;
        const startTime = Math.max(now, nextPlayTimeRef.current);
        source.start(startTime);
        nextPlayTimeRef.current = startTime + buffer.duration;
    }, []);

    // Process received WebSocket messages
    const handleWebSocketMessage = useCallback((event) => {
        try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
                case 'audio.output':
                    if (data.audio) {
                        const float32 = pcm16Base64ToFloat32(data.audio);
                        playAudioChunk(float32);
                    }
                    break;
                    
                case 'text.output':
                    if (data.text) {
                        setTranscriptText(prev => prev + (prev ? '\n' : '') + data.text);
                    }
                    break;
                    
                case 'error':
                    console.error('WebSocket error:', data.message);
                    setCallStatus('error');
                    break;
                    
                default:
                    console.log('Unknown message type:', data.type);
            }
        } catch (err) {
            console.error('Error parsing WebSocket message:', err);
        }
    }, [playAudioChunk]);

    // Setup microphone input and audio processing
    const setupAudioInput = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: SAMPLE_RATE,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                }
            });
            
            mediaStreamRef.current = stream;
            
            // Create audio context for input processing
            const ctx = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: SAMPLE_RATE
            });
            audioContextRef.current = ctx;
            
            const source = ctx.createMediaStreamSource(stream);
            sourceNodeRef.current = source;
            
            // Create ScriptProcessor for capturing audio (or AudioWorklet if available)
            const bufferSize = 4096;
            const processor = ctx.createScriptProcessor(bufferSize, 1, 1);
            processorRef.current = processor;
            
            processor.onaudioprocess = (e) => {
                if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
                
                const inputData = e.inputBuffer.getChannelData(0);
                const base64Audio = float32ToPcm16Base64(inputData);
                
                wsRef.current.send(JSON.stringify({
                    type: 'audio.input',
                    audio: base64Audio
                }));
            };
            
            source.connect(processor);
            processor.connect(ctx.destination);
            
            return true;
        } catch (err) {
            console.error('Error setting up audio input:', err);
            return false;
        }
    }, []);

    // Cleanup audio resources
    const cleanupAudio = useCallback(() => {
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        
        if (sourceNodeRef.current) {
            sourceNodeRef.current.disconnect();
            sourceNodeRef.current = null;
        }
        
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        nextPlayTimeRef.current = 0;
    }, []);

    // Start the call
    const startCall = useCallback(async () => {
        if (isCallActive || isConnecting) return;
        
        setIsConnecting(true);
        setCallStatus('connecting');
        setTranscriptText('');
        
        try {
            // Get auth token
            const token = await getAccessTokenSilently();
            
            // Setup audio before connecting
            const audioReady = await setupAudioInput();
            if (!audioReady) {
                throw new Error('Failed to setup audio input');
            }
            
            // Create WebSocket connection
            const wsUrl = getWebSocketUrl();
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            
            ws.onopen = () => {
                console.log('WebSocket connected, sending session.create');
                
                // Send authentication message
                ws.send(JSON.stringify({
                    type: 'session.create',
                    auth_token: token,
                    assistant_key: agentId
                }));
                
                setIsCallActive(true);
                setIsConnecting(false);
                setCallStatus('active');
            };
            
            ws.onmessage = handleWebSocketMessage;
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                setCallStatus('error');
                setIsConnecting(false);
            };
            
            ws.onclose = (event) => {
                console.log('WebSocket closed:', event.code, event.reason);
                setIsCallActive(false);
                setIsConnecting(false);
                setCallStatus('idle');
                cleanupAudio();
            };
            
        } catch (err) {
            console.error('Error starting call:', err);
            setCallStatus('error');
            setIsConnecting(false);
            cleanupAudio();
        }
    }, [agentId, isCallActive, isConnecting, getAccessTokenSilently, getWebSocketUrl, setupAudioInput, handleWebSocketMessage, cleanupAudio]);

    // End the call
    const endCall = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        
        cleanupAudio();
        setIsCallActive(false);
        setIsConnecting(false);
        setCallStatus('idle');
    }, [cleanupAudio]);

    // Toggle call state
    const toggleCall = useCallback(() => {
        if (isCallActive) {
            endCall();
        } else {
            startCall();
        }
    }, [isCallActive, startCall, endCall]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            endCall();
        };
    }, [endCall]);

    return {
        isCallActive,
        isConnecting,
        callStatus,
        transcriptText,
        startCall,
        endCall,
        toggleCall,
    };
}

export default useRealtimeAudioCall;
